import ConfirmModal from 'components/ConfirmModal/ConfirmModal';
import { AssembleiaStatus } from 'enuns/AssembleiaStatus';
import { AssembleiaAssociadoRoutePath } from 'library/Routes';
import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { Link as RouterLink, LinkProps as RouterLinkProps, useRouteMatch } from 'react-router-dom';
import { modalsActionsOpen } from 'store/modules/ModalsActions';
import { IReducer } from 'store/Reducer';
import { getUniqueKey } from 'utils/KeyUtils';
import PerfilDeslogado from 'store/modules/auth/PerfilDeslogado';
import { AssembleiaPautaStatus } from 'enuns/AssembleiaPautaStatus';
import { samePath } from 'utils/PathUtils';
import { getPermitirVoto } from 'utils/AssembleiaUtils';

interface ICustomRouterLinkLockedAccess {
  title: string,
  message: string
}

export interface ICustomRouterLink {
  to: string,
  onClick: Function
}

export default React.forwardRef<any, Omit<RouterLinkProps, ''>>((props, ref) => {
  const { t } = useTranslation();
  const reducer = useSelector((state: IReducer) => state);
  const [allowChangeRoute, setAllowChangeRoute] = useState(false);
  const matchAssembleiaAssociadoRoute = useRouteMatch({
    path: AssembleiaAssociadoRoutePath,
  });
  const dispatchActions = useDispatch();
  const [eventRef, setEventRef] = useState<React.MouseEvent<HTMLElement> | null>(null);

  const isLogged = useCallback((): boolean => {
    return reducer.auth.logged;
  }, [reducer]);

  const currentAssembleiaAssociadoInLive = useCallback((): boolean => {
    const evento = reducer.assembleias.currentEvent;
    return evento && evento.status === AssembleiaStatus.Live ? true : false;
  }, [reducer]);

  const hasOpenedPautasWithoutVote = useCallback((): boolean => {
    const pautas = reducer.assembleias.currentEvent && reducer.assembleias.currentEvent.pautas ? reducer.assembleias.currentEvent.pautas : [];
    const openedPautas = pautas.filter(
      (pauta): boolean => {
        return pauta.status === AssembleiaPautaStatus.Liberada && pauta.votos && pauta.votos.length === 0 ? true : false;
      }
    )
    const permiteVotar = getPermitirVoto(
      reducer.assembleias.currentEvent && reducer.assembleias.currentEvent.contasDisponiveis ? reducer.assembleias.currentEvent.contasDisponiveis : [],
      reducer.auth.usuario ? reducer.auth.usuario.cpfCnpj : ''
    )
    return openedPautas.length > 0 && permiteVotar ? true : false;
  }, [reducer]);

  const hasPermissionToPautaParticipate = useCallback((): boolean => {
    const perfil = reducer.auth && reducer.auth.usuario ? reducer.auth.usuario!.perfil : PerfilDeslogado;
    return perfil.associadoRoute.assembleias.pautas.participar;
  }, [reducer]);

  const inAssembleiaLive = useCallback((): boolean => {
    if (matchAssembleiaAssociadoRoute) {
      return currentAssembleiaAssociadoInLive();
    }
    return false;
  }, [matchAssembleiaAssociadoRoute, currentAssembleiaAssociadoInLive])

  const executeOnClick = useCallback((e: React.MouseEvent<HTMLElement>) => {
    const newEvent = document.createEvent("MouseEvents");
    newEvent.initMouseEvent("click", true, true, window, 0, 0, 0, 0, 0, false, false, false, false, 0, null);
    e.currentTarget.dispatchEvent(newEvent);
  }, [])

  const showAlert = useCallback((event: React.MouseEvent<HTMLElement>, lockedAccess: ICustomRouterLinkLockedAccess) => {
    const modalKey: string = getUniqueKey();
    dispatchActions(
      modalsActionsOpen(
        ConfirmModal,
        {
          genericProps: {
            title: lockedAccess.title,
            contentText: lockedAccess.message,
            key: modalKey,
            confirmCallback: () => {
              setAllowChangeRoute(true);
              setEventRef(event);
            }
          }
        },
        modalKey
      )
    )
  }, [setAllowChangeRoute, dispatchActions]);

  useEffect(() => {
    if (eventRef && allowChangeRoute) {
      executeOnClick(eventRef);
      setEventRef(null);
    }
  }, [allowChangeRoute, eventRef, executeOnClick])

  const getLockedAccess = useCallback((): ICustomRouterLinkLockedAccess | null => {
    const path = props.to;
    if (!allowChangeRoute && !samePath((typeof path === 'string' ? path : null), window.location.pathname)) {
      if (isLogged() && inAssembleiaLive() && hasOpenedPautasWithoutVote() && hasPermissionToPautaParticipate()) {
        return {
          title: t('FugaAlert.assembleiaAssociadoLiveTitle'),
          message: t('FugaAlert.assembleiaAssociadoLiveMessage')
        }
      }
    }
    return null;
  }, [t, props, allowChangeRoute, isLogged, inAssembleiaLive, hasOpenedPautasWithoutVote, hasPermissionToPautaParticipate]);

  const getCurrentProps = useCallback(() => {
    return {
      ...props,
      onClick: (e: React.MouseEvent<HTMLElement>) => {
        const newEvent: React.MouseEvent<HTMLElement> = { ...e };
        const lockedAccess = getLockedAccess();
        if (lockedAccess) {
          e.preventDefault();
          showAlert(newEvent, lockedAccess);
        } else {
          setAllowChangeRoute(false);
        }
        if (typeof props.onClick === 'function') {
          props.onClick(e as React.MouseEvent<HTMLAnchorElement>);
        }
      }
    }
  }, [props, showAlert, getLockedAccess, setAllowChangeRoute]);

  return <RouterLink ref={ref} to="" { ...getCurrentProps() } />
})